import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams, useLocation} from "react-router-dom"
import {useKeyboard} from '../../../../hooks/keyboard';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {useMapSelector} from '../../../../hooks/map-selector';
import {useFilterSelector} from "../../../../hooks/filter-selector"
import {useMap} from "../../../../hooks/map"
import {DoorModel} from '../../../../types/door';
import {SelectItem} from '../../../../components/Select';
import {DoorBrandModel} from '../../../../types/door-brand';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {isValidDoorNumber} from '../../../../utils/validation';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';
import {useLocalization} from "../../../../hooks/localization";
import {useSetSelector} from "../../../../hooks/set-selector";
import {BridgeDeviceModel} from "../../../../types/bridge-device";
import {
    INPUT_TYPE_ID_SENSOR,
    InputModel,
    InputTypeModel,
} from "../../../../types/control"

const BACK_URL = '/settings/door';
type CONTROL_VALUE = {
    id: string
    isVirtual: boolean
}
export const DoorEditScreen = memo(function DoorEditScreen() {
  const { t } = useLocalization();
  const params = useParams<{ id: string }>();
  const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
  const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
  const { success, failure } = useMessage();
  const [rpc] = useRPC();
  const history = useHistory();
  const location = useLocation<{ controlId: string; bridgeDeviceId: string; inputTypeId: string; input: InputModel }>()
  const doorList = useSelector(state => state.control.doorList);
  const doorBranchOptionList: SelectItem<DoorBrandModel>[] = useMapSelector(
    state => state.control.doorBrandList,
    useCallback(doorBrand => ({ value: doorBrand, label: doorBrand.name }), []),
  ) || [];

    const [selected, setSelected] = useState<DoorModel>(null);
    const [name, setName] = useState('');
    const [doorBrandOption, setDoorBrandOption] = useState<SelectItem<DoorBrandModel>>(null);
    const [doorPath, setDoorPath] = useState('');
    const [doorNumber, setDoorNumber] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

  const inputList = useSelector(state => state.control.inputList);
  const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
  const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
  // These devices are required to have at least one input
  const inputRequiredDeviceTypeIds = useMemo(() => [
    "21", // AO1710
    "22", // AO1730
    "18", // Booster
    "12", // Home Manager
    "19", // IM2008D
    "15", // IM1704D
    "23", // STS Plus
  ], []);
  // These devices are not required to have at least one input.
  const inputNotRequiredDeviceTypeIds = useMemo(() => [
    "11", // PAN-1707 (Nova panel old version)
    "20", // PAN-19xx (Nova panel new version)
    "14", // RL-1710
    "1",  // RL-1720
    "25", // CCM-2312
    "2",  // CCM-1760
    "26", // CCM-2324
  ], []);
  const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMemo(
    () =>
      bridgeDeviceList
        .filter(
          bridgeDevice => {
            const inputCount = bridgeDeviceTypeSet[bridgeDevice.type_id]?.input_count ?? 0;
            const isInputRequiredDevice = inputRequiredDeviceTypeIds.includes(bridgeDevice.type_id);
            const isInputNotRequiredDevice = inputNotRequiredDeviceTypeIds.includes(bridgeDevice.type_id);
            return (isInputRequiredDevice && inputCount > 0) || isInputNotRequiredDevice;
          }
        )
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        .map(bridgeDevice => ({
          value: bridgeDevice,
          label: bridgeDevice.name
        })),
    [bridgeDeviceList, bridgeDeviceTypeSet, inputRequiredDeviceTypeIds, inputNotRequiredDeviceTypeIds]
  )
  const selectedDevice = useMemo(() => location.state?.input || inputList.find(control => control.id === params?.id) || null, []);
  const initialBridgeDevice = useMemo(() => {
    if (selectedDevice == null) {
      if (location?.state?.bridgeDeviceId == null) {
        return null;
      }
      return bridgeDeviceOptionList.find(
        bridgeDeviceOption => bridgeDeviceOption.value.id === location?.state?.bridgeDeviceId
      ) || null;
    }
    return bridgeDeviceOptionList.find(
      bridgeDeviceOption => bridgeDeviceOption.value.id === selectedDevice.bridge_device_id
    ) || null;
  }, [selectedDevice, location?.state?.bridgeDeviceId, bridgeDeviceOptionList]);

  const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(initialBridgeDevice);
  const controlSet = useSetSelector(state => state.control.controlList)
  const inputTypeList = useSelector(state => state.control.inputTypeList);
  const inputTypeOptionList: SelectItem<InputTypeModel>[] =
    useMemo(() => inputTypeList
      .map(inputType => ({
        value: inputType,
        label: inputType.name,
      })), [inputTypeList]);
  const [inputType, setInputType] = useState<SelectItem<InputTypeModel>>(
    (selectedDevice == null ? (location?.state?.inputTypeId == null ? null :
      inputTypeOptionList.find(inputTypeOption => inputTypeOption.value.id === location.state.inputTypeId)
      || null) : inputTypeOptionList.find(inputTypeOption => inputTypeOption.value.id === selectedDevice.type_id)
      || null),
  );
  const isInputTypeSensor = useMemo(() => inputType?.value?.id === INPUT_TYPE_ID_SENSOR || false, [inputType])
  const bridgeDeviceFilteredControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
    useFilterSelector(
      state => state.control.controlList,
      control => {
        const isDeviceControl = control.bridge_device_id === bridgeDevice?.value.id;
        const isDoorControl = ["16", "21"].includes(control.type_id);
        return isDeviceControl && isDoorControl;
      },
      [isInputTypeSensor, bridgeDevice?.value]
    ),
    controlModel => ({ label: controlModel.name, value: { isVirtual: false, id: controlModel.id } }),
    []
  )
  const bridgeDeviceFilteredVirtualControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
    useSelector(
      state => state.control.virtualControlList,
      /*virtualControl =>
          virtualControl.bridge_device_id === bridgeDevice?.value.id &&
          (isInputTypeSensor
              ? controlSet[virtualControl.control_id]?.type_id === CONTROL_TYPE_ID_ALARM
              : controlSet[virtualControl.control_id]?.type_id !== CONTROL_TYPE_ID_ALARM &&
                controlSet[virtualControl.control_id]?.type_id !== CONTROL_TYPE_ID_SIREN),
      [isInputTypeSensor, bridgeDevice?.value]*/
    ),
    virtualControl => ({
      label: controlSet[virtualControl.control_id]?.name || "",
      value: { isVirtual: true, id: virtualControl.id }
    }),
    [controlSet]
  )
  const controlOptionList: SelectItem<CONTROL_VALUE>[] = useMemo(
    // Removed virtual controls from the list. If it's the way, we need to remove virtual control handling from the code.
    () => [
      ...bridgeDeviceFilteredControlOptionList,
      // ...bridgeDeviceFilteredVirtualControlOptionList
    ],
    [
      bridgeDeviceFilteredControlOptionList,
      //bridgeDeviceFilteredVirtualControlOptionList
    ]
  )

    const [controlOption, setControlOption] = useState<SelectItem<CONTROL_VALUE>>(
        selectedDevice == null ? null : (
            selectedDevice?.is_virtual_control ?
                bridgeDeviceFilteredVirtualControlOptionList.find(option => option.value.id === selectedDevice?.relational_id) :
                bridgeDeviceFilteredControlOptionList.find(option => option.value.id === selectedDevice?.relational_id)
        ) || null,
    );

    useEffect(() => {
        setControlOption(null)
    }, [bridgeDevice])


    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

  const isFieldsValid = useCallback((): boolean => {
    if (name == null || name.length === 0) {
      failure(t('pls_enter_a_valid_door_name'));
      return false;
    }

        if (doorBrandOption == null) {
            failure(t('pls_select_a_valid_door_brand'));

            return false;
        }

        if ((doorPath == null || doorPath.length === 0) && doorBrandOption?.value?.name !== "RF" && doorBrandOption?.value?.name !== "DTMF") {
            failure(t('pls_enter_a_valid_url'));

            return false;
        }

        if (doorBrandOption?.value?.name !== "Orion M1" && doorBrandOption?.value?.name !== "RF" && doorBrandOption?.value?.name !== "DTMF") {
            if (!isValidDoorNumber(doorNumber)) {
                failure(t("pls_enter_a_valid_door_number"))

                return false
            }
            if (username == null || username.length === 0) {
                failure(t("pls_enter_a_valid_username"))

                return false
            }
            if ((password == null || password.length === 0) && doorBrandOption?.value?.name !== "EIO") {
                failure(t("pls_enter_a_valid_password"))

                return false
            }
        }

        if (doorBrandOption?.value?.name === "RF") {
            if (bridgeDevice == null) {
                failure(t("pls_select_device"))

                return false
            }
            if(controlOption == null){
                failure(t("pls_enter_control_name"))

                return false
            }
        }

        if (doorBrandOption?.value?.name === "DTMF") {
            if (username == null || username.length === 0) {
                failure(t("door_edit.pls_enter_a_valid_tone_value"))

                return false
            }
        }

        return true;
    }, [t, doorBrandOption, doorPath, doorNumber, failure, name, password, username, bridgeDevice, controlOption]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        let doorPathWithScheme = doorPath;

        if (doorPathWithScheme.indexOf('http://') !== 0) {
            doorPathWithScheme = 'http://' + doorPath;
        }

        const doorModel: DoorModel = {
            id: selected?.id || null,
            name,
            door_brand_id: doorBrandOption.value.id,
            door_path: (doorBrandOption?.value?.name === "RF" && controlOption?.label) || (doorBrandOption?.value?.name === "DTMF" && username) || doorPathWithScheme,
            door_number: Number(doorNumber),
            username: doorBrandOption?.value?.name === "RF" ? bridgeDevice?.value?.id : username,
            password: doorBrandOption?.value?.name === "RF" ? controlOption?.value?.id : password
        };

    return rpc.updateDoor(doorModel)
      .then(() => {
        success(t(doorModel.id ? 'door_successfully_edited' : 'door_successfully_created'));
        onBackPressed();
      }).catch(failure);
  }, [
    t,
    controlOption?.value?.id,
    bridgeDevice?.value?.id,
    doorPath,
    doorNumber,
    failure,
    isFieldsValid,
    name,
    doorBrandOption,
    onBackPressed,
    password,
    rpc,
    selected?.id,
    success,
    username,
    controlOption?.label
  ]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteDoor(selected.id)
            .then(() => {
                success(t('door_successfully_deleted'));
                onBackPressed();
            })
            .catch(failure);
    }, [t, failure, onBackPressed, rpc, selected, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    const buttons = useMemo(() => [
        {
            component: selected == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type='door'
                    questionText={t('are_you_sure_you_want_to_delete')}
                    questionTextRelational={message => t('this_will_be_deleted_with_this_message__continue',{name:selected?.name, message})}
                    onPress={onDeletePressed}/>,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        }], [t, selected, onSavePressed, onDeletePressed],
    );

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const door = doorList.find(doorItem => doorItem.id === params['id']);
        if (door == null) {
            return;
        }

    setSelected(door);
    setName(door.name);
    setDoorBrandOption(
      doorBranchOptionList.find(doorBranchOption => doorBranchOption.value.id === door.door_brand_id) ||
      doorBranchOptionList[0] ||
      null
    );
    setDoorPath(door.door_path);
    setDoorNumber(door.door_number.toString());
    setUsername(door.username);
    setPassword(door.password);
    setBridgeDevice(bridgeDeviceOptionList.find(b => b.value.id === door.username) || null)
    setControlOption(controlOptionList.find(c => c.value.id === door.password) || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setDoorBrandOption(doorBranchOptionList[0] || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_door_name')}
                            label={t('door_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('brand_model')}
                            options={doorBranchOptionList}
                            onChange={setDoorBrandOption}
                            value={doorBrandOption}/>
                            {
                                renderIf(doorBrandOption?.value?.name !== "Orion M1" && doorBrandOption?.value?.name !== "RF" && doorBrandOption?.value?.name !== "DTMF")(()=> (
                                    <SettingInputComponent
                                        placeholder={t('write_relay_number')}
                                        label={t('relay_number')}
                                        value={doorNumber}
                                        setValue={setDoorNumber}
                                        setActiveInput={setActiveNumpadInput}
                                        onBlur={onBlur}/>))
                            }
                            {
                                renderIf(doorBrandOption?.value?.name !== "RF" && doorBrandOption?.value?.name !== "DTMF")(()=> (
                                    <SettingInputComponent
                                        maxLength={200}
                                        placeholder={t('write_url')}
                                        label={t('url_ip')}
                                        value={doorPath}
                                        setValue={setDoorPath}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>))
                            }
                    </div>
                    <div className='content-wrapper'>
                    {
                        renderIf(doorBrandOption?.value?.name !== "Orion M1" && doorBrandOption?.value?.name !== "RF")(() => (
                            <>
                                <SettingInputComponent
                                    placeholder={doorBrandOption?.value?.name === "DTMF" ? t('door_edit.enter_tone_value') : doorBrandOption?.value?.name === "EIO" ? t('pls_enter_a_pulse') : t('write_username')}
                                    label={doorBrandOption?.value?.name === "DTMF" ? 'Tone' : doorBrandOption?.value?.name === "EIO" ? 'Pulse' : t('username')}
                                    value={username}
                                    setValue={setUsername}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}/>
                            {
                                renderIf(doorBrandOption?.value?.name !== "DTMF" && doorBrandOption?.value?.name !== "EIO")(() => (
                                <SettingInputComponent
                                    type='password'
                                    placeholder={t('write_password')}
                                    label={t('password')}
                                    value={password}
                                    setValue={setPassword}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}/>
                                ))
                            }
                            </>
                        ))
                    }
                    {
                        renderIf(doorBrandOption?.value?.name === "RF")(() => (
                            <>
                                <SettingSelectComponent
                                    label={t('device')}
                                    options={bridgeDeviceOptionList}
                                    onChange={setBridgeDevice}
                                    value={bridgeDevice}/>
                                <SettingSelectComponent
                                    label={t('control')}
                                    options={controlOptionList}
                                    onChange={setControlOption}
                                    value={controlOption}/>
                            </>
                        ))
                    }
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
